import React, { useRef, useState, useEffect, useMemo } from 'react';

interface AutoSizingViewportProps {
  platform: string;
  children: React.ReactNode;
  className?: string;
}

// Platform-specific height fallbacks (removed - unused)
// const PLATFORM_HEIGHT_FALLBACKS = {
//   // TikTok / Instagram Reels (9:16): Much taller aspect ratio
//   tiktok: (width: number) => Math.min(1600, Math.max(800, width * 16/9)),
//   'instagram-reel': (width: number) => Math.min(1600, Math.max(800, width * 16/9)),
//   
//   // Instagram Posts (square/4:5): More generous height
//   instagram: (width: number) => Math.min(1000, Math.max(600, width * 1.0)),
//   
//   // Twitter/X: More generous height
//   twitter: (width: number) => Math.min(1000, Math.max(600, width * 1.1)),
//   
//   // Facebook / LinkedIn: More generous height
//   facebook: (width: number) => Math.min(1200, Math.max(700, width * 1.2)),
//   linkedin: (width: number) => Math.min(1200, Math.max(700, width * 1.2)),
//   
//   // Generic fallback
//   'generic-iframe': (width: number) => Math.min(800, Math.max(400, width * 9/16)),
//   
//   // LinkPreview: 72–96px (compact chip)
//   'og-preview': () => 84, // Compact chip height
//   'link': () => 84, // Compact chip height
// } as const;

export const AutoSizingViewport: React.FC<AutoSizingViewportProps> = ({ 
  platform, 
  children, 
  className = '' 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setContainerWidth] = useState(600); // Default fallback

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerWidth(rect.width || 600);
      }
    };

    // Initial measurement
    updateWidth();

    // Set up ResizeObserver for dynamic width changes
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Fallback to window resize
    window.addEventListener('resize', updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const viewportStyle = {
    position: 'relative' as const,
    display: 'block',
    width: '100%',
    height: '100%',  // << fill the card
    overflow: 'hidden',
  };

  const scrollPaneStyle = useMemo(() => {
    return {
      width: '100%',
      height: '100%',
      // No internal scrolling - let content determine size
      overflow: 'visible',
      boxSizing: 'border-box' as const
    };
  }, []);

  /* Do NOT vertically center; let embeds start at the very top-left */
  const contentWrapperStyle = useMemo(() => ({
    display: 'block',
    width: '100%',
    height: '100%'
  }), []);

  return (
    <div ref={containerRef} data-platform={platform} style={viewportStyle} className={className}>
      <div style={scrollPaneStyle}>
        <div style={contentWrapperStyle}>{children}</div>
      </div>
    </div>
  );
};
